import { useMemo } from 'react';
import { geoMercator, geoPath, geoCircle } from 'd3-geo';
import { feature } from 'topojson-client';
import { useWorldAtlas } from '../../../hooks/useWorldAtlas';

// Mercator world map with target countries highlighted by region.priority.
// mode='countries' shows region fills + labels; mode='radius' shows concentric
// distance rings from geo.home with muted country fills and a home pin.

const W = 800;
const H = 420;

const PRIORITY_FILL = {
  primary:   'var(--ink)',
  secondary: 'var(--accent)',
  tertiary:  'var(--ink-4)',
};

export function NicheMap({ geo, mode = 'countries' }) {
  const { topo, loading, error } = useWorldAtlas();

  const { features, path, projection } = useMemo(() => {
    if (!topo) return { features: [], path: null, projection: null };
    const fc = feature(topo, topo.objects.countries);
    const proj = geoMercator()
      .scale(120)
      .translate([W / 2, H / 1.7])
      .center([10, 30]);
    return { features: fc.features, path: geoPath(proj), projection: proj };
  }, [topo]);

  const targetSet = new Set(geo.regions.map((r) => r.name));
  const priorityOf = (name) => geo.regions.find((r) => r.name === name)?.priority;

  if (error) {
    return (
      <div className="grid place-items-center h-full font-mono text-mono-tag uppercase text-danger">
        MAP UNAVAILABLE
      </div>
    );
  }
  if (loading || !topo || !path) {
    return (
      <div className="grid place-items-center h-full font-mono text-mono-tag uppercase text-ink-3">
        LOADING MAP…
      </div>
    );
  }

  const fillFor = (name) => {
    if (mode === 'radius') return 'var(--paper-3)';
    if (!targetSet.has(name)) return 'var(--paper-3)';
    return PRIORITY_FILL[priorityOf(name)] ?? 'var(--ink)';
  };

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="xMidYMid meet"
      className="block w-full h-full"
    >
      <defs>
        <pattern id="oceanGrid" width="20" height="20" patternUnits="userSpaceOnUse">
          <path d="M 20 0 L 0 0 0 20" fill="none" stroke="rgba(17,18,20,0.10)" strokeWidth="0.5" />
        </pattern>
        <pattern id="oceanDots" width="6" height="6" patternUnits="userSpaceOnUse">
          <circle cx="1" cy="1" r="0.6" fill="rgba(17,18,20,0.15)" />
        </pattern>
      </defs>

      <rect width={W} height={H} fill="url(#oceanDots)" />
      <rect width={W} height={H} fill="url(#oceanGrid)" />

      {/* Countries */}
      <g>
        {features.map((f, i) => {
          const name = f.properties.name;
          const targeted = mode === 'countries' && targetSet.has(name);
          return (
            <path
              key={i}
              d={path(f) || ''}
              fill={fillFor(name)}
              stroke="var(--ink)"
              strokeWidth={targeted ? 0.8 : 0.4}
              opacity={targeted ? 1 : 0.85}
            />
          );
        })}
      </g>

      {/* Radius rings (radius mode only) */}
      {mode === 'radius' && geo.rings?.map((ring) => {
        const deg = ring.km / 111.32;
        const circleFn = geoCircle()
          .center([geo.home.lon, geo.home.lat])
          .radius(deg);
        const d = path(circleFn());
        const stroke = PRIORITY_FILL[ring.priority] ?? 'var(--ink-4)';
        return (
          <path key={ring.label} d={d || ''}
            fill={stroke} fillOpacity={0.06}
            stroke={stroke} strokeOpacity={0.85}
            strokeWidth={ring.priority === 'primary' ? 1.4 : 1}
            strokeDasharray={ring.priority === 'tertiary' ? '3 3' : undefined}
          />
        );
      })}

      {/* Home pin (always rendered when home is available) */}
      {(() => {
        if (!geo.home || !projection) return null;
        const pt = projection([geo.home.lon, geo.home.lat]);
        if (!pt || Number.isNaN(pt[0])) return null;
        return (
          <g transform={`translate(${pt[0]}, ${pt[1]})`}>
            <circle r="10" fill="var(--paper)" stroke="var(--ink)" strokeWidth="0.6" opacity="0.7" />
            <circle r="5" fill="var(--accent)" stroke="var(--ink)" strokeWidth="1" />
            <circle r="1.5" fill="var(--ink)" />
            <text x={9} y={-8} fontFamily="var(--mono)" fontSize="9"
              fill="var(--ink)" style={{ letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              HOME · {(geo.home.city || '').toUpperCase()}
            </text>
          </g>
        );
      })()}

      {/* Country labels for targeted regions (countries mode only) */}
      {mode === 'countries' && (
        <g
          fontFamily="var(--mono)"
          fontSize="9"
          style={{ textTransform: 'uppercase', letterSpacing: '0.08em' }}
        >
          {features
            .filter((f) => targetSet.has(f.properties.name))
            .map((f, i) => {
              const c = path.centroid(f);
              if (!c || Number.isNaN(c[0])) return null;
              const name = f.properties.name;
              const pri = priorityOf(name);
              const isPrimary = pri === 'primary';
              return (
                <g key={i}>
                  <circle
                    cx={c[0]}
                    cy={c[1]}
                    r={isPrimary ? 3 : 2}
                    fill={isPrimary ? 'var(--paper)' : 'var(--ink)'}
                    stroke="var(--ink)"
                    strokeWidth="0.6"
                  />
                  <text
                    x={c[0] + 5}
                    y={c[1] + 3}
                    fill={isPrimary ? 'var(--paper)' : 'var(--ink)'}
                  >
                    {name}
                  </text>
                </g>
              );
            })}
        </g>
      )}

      {/* Legend bottom-left */}
      <g
        transform={`translate(16, ${H - 22})`}
        fontFamily="var(--mono)"
        fontSize="9"
        fill="var(--ink-3)"
        style={{ letterSpacing: '0.08em', textTransform: 'uppercase' }}
      >
        <rect x="0"   y="-8" width="10" height="10" fill="var(--ink)"    stroke="var(--ink)" />
        <text x="14"  y="0">Primary</text>
        <rect x="80"  y="-8" width="10" height="10" fill="var(--accent)" stroke="var(--ink)" />
        <text x="94"  y="0">Secondary</text>
        <rect x="180" y="-8" width="10" height="10" fill="var(--ink-4)"  stroke="var(--ink)" />
        <text x="194" y="0">Tertiary</text>
      </g>
    </svg>
  );
}

export default NicheMap;
